import React from 'react';
import { useDragLayer } from 'react-dnd';
import type { XYCoord } from 'react-dnd';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Transaction } from '../types';

const ItemTypes = {
  TRANSACTION: 'transaction' as const,
  CATEGORY: 'category' as const
};

function getItemStyles(
  initialOffset: XYCoord | null,
  currentOffset: XYCoord | null,
) {
  if (!initialOffset || !currentOffset) {
    return {
      display: 'none'
    };
  }

  const { x, y } = currentOffset;

  return {
    transform: `translate(${x}px, ${y}px) rotate(-2deg)`,
    WebkitTransform: `translate(${x}px, ${y}px) rotate(-2deg)`
  };
}

export const CustomDragLayer: React.FC = () => {
  const {
    itemType,
    isDragging,
    item,
    initialOffset,
    currentOffset
  } = useDragLayer((monitor) => ({
    item: monitor.getItem(),
    itemType: monitor.getItemType(),
    initialOffset: monitor.getInitialSourceClientOffset(),
    currentOffset: monitor.getSourceClientOffset(),
    isDragging: monitor.isDragging()
  }));

  if (!isDragging) {
    return null;
  }

  if (!item || itemType !== ItemTypes.TRANSACTION) {
    return null;
  }

  const transaction = item as Transaction;
  
  const formattedAmount = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(Math.abs(transaction.amount));

  const getAmountDisplay = () => {
    const isNegative = transaction.amount < 0;
    
    if (transaction.type === 'expense') {
      // Expenses are always shown as negative
      return `-${formattedAmount}`;
    } else if (transaction.type === 'income') {
      // Income is always shown as positive
      return `+${formattedAmount}`;
    } else if (transaction.type === 'savings') {
      // Savings: positive for deposits, negative for withdrawals
      return isNegative ? `-${formattedAmount}` : `+${formattedAmount}`;
    }
    
    // Default fallback
    return isNegative ? `-${formattedAmount}` : `+${formattedAmount}`;
  };

  // Helper function to get amount color based on transaction type and amount
  const getAmountColor = (type: string, amount: number) => {
    if (type === 'expense') {
      return 'error';
    } else if (type === 'income') {
      return 'success';
    } else if (type === 'savings') {
      // Savings: red for withdrawals (negative), primary for deposits (positive)
      return amount < 0 ? 'error' : 'primary';
    }
    return 'text.primary';
  };

  // Helper function to get badge styles based on transaction type and amount
  const getBadgeStyles = (type: string, amount: number) => {
    if (type === 'income') {
      return {
        backgroundColor: (theme: any) => alpha(theme.palette.success.main, 0.1),
        color: (theme: any) => theme.palette.success.main
      };
    } else if (type === 'expense') {
      return {
        backgroundColor: (theme: any) => alpha(theme.palette.error.main, 0.1),
        color: (theme: any) => theme.palette.error.main
      };
    } else if (type === 'savings') {
      // Savings: red for withdrawals (negative), primary for deposits (positive)
      if (amount < 0) {
        return {
          backgroundColor: (theme: any) => alpha(theme.palette.error.main, 0.1),
          color: (theme: any) => theme.palette.error.main
        };
      } else {
        return {
          backgroundColor: (theme: any) => alpha(theme.palette.primary.main, 0.1),
          color: (theme: any) => theme.palette.primary.main
        };
      }
    }
    return {
      backgroundColor: (theme: any) => alpha(theme.palette.grey[500], 0.1),
      color: (theme: any) => theme.palette.grey[500]
    };
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        pointerEvents: 'none',
        zIndex: 9999,
        left: 0,
        top: 0,
        width: '100%',
        height: '100%'
      }}
    >
      <Box
        sx={{
          ...getItemStyles(initialOffset, currentOffset),
          position: 'absolute',
          backgroundColor: theme => theme.palette.background.paper,
          boxShadow: theme => theme.shadows[8],
          borderRadius: theme => theme.shape.borderRadius,
          p: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          minWidth: 300,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          willChange: 'transform'
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'text.primary', flex: 1 }}>
          {transaction.description}
        </Typography>
        <Typography
          variant="body2"
          color={getAmountColor(transaction.type, transaction.amount)}
          sx={{ fontWeight: 'medium', whiteSpace: 'nowrap' }}
        >
          {getAmountDisplay()}
        </Typography>
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            ...getBadgeStyles(transaction.type, transaction.amount),
            borderRadius: '16px',
            px: 1,
            py: 0.25,
            fontSize: '0.75rem',
            fontWeight: 500,
            whiteSpace: 'nowrap'
          }}
        >
          {transaction.category}
        </Box>
      </Box>
    </Box>
  );
}; 